/**
 * ELO (Bronze → Platina) — APRESENTAÇÃO, não regra de negócio.
 *
 * ## W3-7 — decisão: (b), e a premissa da tarefa não se confirmou
 *
 * A tarefa dizia que este arquivo duplica `getLevelInfo`
 * (`api/src/database/queries/student-xp.queries.ts`). Não duplica — as duas
 * respondem perguntas diferentes:
 *
 * - `getLevelInfo(totalXp)` responde **"quanto XP dá qual nível"**. É a curva
 *   `nível² × (nível + 100) / 10`, e é regra de negócio de verdade: ela decide
 *   o `current_level` que fica GRAVADO em `customer_stats`.
 * - este arquivo responde **"que medalha eu pinto em cima de um nível"**. Nome
 *   e classe do Tailwind. Nada aqui é persistido nem comparado com o banco.
 *
 * A api não tem conceito de elo — `Bronze|Prata|Ouro|Platina` não aparece em
 * lugar nenhum de `api/src`. Ou seja: não havia duas fontes para a mesma
 * verdade; havia uma verdade (o nível) e uma decoração dela.
 *
 * ## Por que (b) e não (a) — "a api devolve o tier junto do XP"
 *
 * O tier é um par (rótulo em português, classe de cor do Tailwind). Colocar
 * isso na api significa: (1) uma decisão de UI passa a morar na camada de
 * banco; (2) recolorir um badge vira deploy da api; (3) todo payload que já
 * carrega `currentLevel` passa a carregar um campo derivado dele — dado
 * redundante que pode chegar dessincronizado do próprio nível que o acompanha.
 * Cosmético que só o cliente lê é cliente que resolve.
 *
 * ## O acoplamento que SOBRA (e é o que o teste congela)
 *
 * A dependência real não é a fórmula: é a UNIDADE. Os cortes abaixo estão em
 * NÍVEL, e a escala de nível pertence à api. Se `calculateXpForLevel` mudar,
 * "Bronze até 25" continua compilando e continua exibindo — mas passa a
 * significar outro tanto de esforço, silenciosamente.
 *
 * Por isso cada corte carrega, no comentário, o XP que ele custa HOJE pela
 * curva da api. É o par espelho do `CLAUDE.md`: mexeu em `calculateXpForLevel`,
 * volte aqui e confira se os degraus ainda fazem sentido. `tiers.test.ts`
 * guarda esses números como literais — quem mudar a curva encontra a
 * divergência escrita, em vez de descobrir pelo aluno reclamando que virou
 * Prata do nada.
 *
 * Arquivo-espelho: `api/src/database/queries/student-xp.queries.ts`
 * (`calculateXpForLevel` / `calculateLevelFromXp`).
 */

export interface Tier {
  readonly name: string
  /** Último nível que ainda pertence a este elo (inclusive). */
  readonly max: number
  readonly color: string
}

export const TIERS: readonly Tier[] = [
  // nível 25 = 7.812 XP pela curva atual da api
  { name: 'Bronze', max: 25, color: 'text-amber-600' },
  // nível 50 = 37.500 XP
  { name: 'Prata', max: 50, color: 'text-slate-400' },
  // nível 75 = 98.437 XP
  { name: 'Ouro', max: 75, color: 'text-yellow-500' },
  { name: 'Platina', max: Infinity, color: 'text-cyan-400' },
] as const

/**
 * O `Number.isFinite` não é paranoia: `stats` chega de `useQuery` e a versão
 * anterior fazia só `TIERS.find((t) => level <= t.max)`. Com `level` NaN — que
 * é o que `undefined` vira em qualquer aritmética, e o que uma resposta ainda
 * não carregada produz — TODA comparação dá false, o `find` devolve undefined e
 * o fallback `TIERS[TIERS.length - 1]` entregava PLATINA. O nível desconhecido
 * exibia o elo mais alto, exatamente ao contrário do que deveria.
 */
export function getTier(level: number): Tier {
  const safeLevel = Number.isFinite(level) ? level : 1
  return TIERS.find((t) => safeLevel <= t.max) ?? TIERS[0]
}
